/** Assisted configuration editor — schema-driven inline grid.
 *
 * Source of truth: the backend-served runtime schema
 * (GET /tour_operators/configs/schema/ → `.top.properties`, fetched through
 * `useTopConfigSchema` and cache-shared with the batch config page).
 *
 * Behaviour driven entirely by the schema:
 *  - Each property present in the live config appears as a grid row.
 *  - Nested objects expand in-place as indented child rows.
 *  - Unknown keys (not in schema) appear in a separate section at the bottom.
 *  - Selecting a row shows its description / default / type in a right pane.
 *  - Adding a property is restricted to schema-defined keys not yet present.
 */
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { ConfirmDialog } from '@/components/ConfirmDialog';

import { AddPropertyPicker } from './AddPropertyPicker';
import { AuthMethodsCell } from './AuthMethodsCell';
import type { AuthMethod } from './AuthMethodEditDialog';
import { ImportConfigWizard } from './ImportConfigWizard';
import { SaveConfigChangesDialog } from './SaveConfigChangesDialog';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as integrationsApi from '@/api/integrations';
import * as topsApi from '@/api/tourOperators';
import type { TourOperatorDetail } from '@/api/tourOperators';
import { useSnackbar } from '@/components/Snackbar';
import { usePermissions } from '@/hooks/usePermissions';

import { useTopConfigSchema } from './useTopConfigSchema';

// ---------------------------------------------------------------------------
// Schema types
// ---------------------------------------------------------------------------

interface SchemaProp {
  type?: 'boolean' | 'integer' | 'number' | 'string' | 'array' | 'object';
  description?: string;
  default?: unknown;
  enum?: unknown[];
  // Positional labels — labels[i] is the human label for enum[i].
  'x-enum-labels'?: string[];
  'x-unit'?: string;
  'x-format-hint'?: string;
  'x-tenant-dependent'?: boolean;
  'x-default-source'?: string;
  properties?: Record<string, SchemaProp>;
  items?: SchemaProp;
  $comment?: string;
  additionalProperties?: boolean;
}

/** Look up an enum value's display label by matching against `prop.enum`. */
function enumLabel(prop: SchemaProp, value: unknown): string | undefined {
  if (!prop.enum || !prop['x-enum-labels']) return undefined;
  const idx = prop.enum.findIndex((e) => e === value);
  return idx >= 0 ? prop['x-enum-labels'][idx] : undefined;
}

// ---------------------------------------------------------------------------
// Config row model
// ---------------------------------------------------------------------------

interface ConfigRow {
  id: string;           // dot-joined path, e.g. "email.reply_to"
  path: string[];
  depth: number;        // 0 = top-level, 1 = child of nested object
  key: string;          // leaf key name
  schema: SchemaProp | null;
  value: unknown;
  isGroup: boolean;     // nested object that can be expanded
  isExpanded: boolean;
  isUnknown: boolean;   // present in config but absent from schema
  isAbsent: boolean;    // schema-defined nested key NOT present in the live config
}

// ---------------------------------------------------------------------------
// Row builder
// ---------------------------------------------------------------------------

function buildRows(
  schemaProps: Record<string, SchemaProp>,
  config: Record<string, unknown>,
  expanded: Set<string>,
  search: string,
  hiddenKeys: Set<string>,
): { schema: ConfigRow[]; unknown: ConfigRow[] } {
  const schemaRows: ConfigRow[] = [];
  const unknownRows: ConfigRow[] = [];
  const lc = search.toLowerCase();

  const matchesSearch = (key: string, prop: SchemaProp | null) =>
    !search ||
    key.toLowerCase().includes(lc) ||
    (prop?.description ?? '').toLowerCase().includes(lc);

  // Schema-defined keys present in config — sorted alphabetically
  const schemaEntries = Object.entries(schemaProps).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  for (const [key, prop] of schemaEntries) {
    if (!(key in config)) continue;
    const value = config[key];
    // A schema-typed object is expandable whenever the live value is a
    // non-null plain object — even if the schema doesn't declare child
    // `properties` (e.g. `curatedplanet`, which is `additionalProperties: true`).
    // In that case the children come purely from the config blob and render
    // as "unknown" children (no per-key schema description).
    const isGroup =
      prop.type === 'object' &&
      value != null &&
      typeof value === 'object' &&
      !Array.isArray(value);

    const rowId = key;
    const row: ConfigRow = {
      id: rowId,
      path: [key],
      depth: 0,
      key,
      schema: prop,
      value,
      isGroup,
      isExpanded: expanded.has(rowId),
      isUnknown: false,
      isAbsent: false, // top-level rows are only emitted when `key in config`
    };

    const childMatches =
      search &&
      isGroup &&
      (() => {
        const schemaKids = prop.properties ? Object.keys(prop.properties) : [];
        const configKids = Object.keys((value as Record<string, unknown>) ?? {});
        const all = new Set<string>([...schemaKids, ...configKids]);
        return [...all].some(
          (ck) =>
            ck.toLowerCase().includes(lc) ||
            (prop.properties?.[ck]?.description ?? '').toLowerCase().includes(lc),
        );
      })();

    if (!matchesSearch(key, prop) && !childMatches) continue;
    schemaRows.push(row);

    // Children for expanded groups — sorted alphabetically
    if (isGroup && (expanded.has(rowId) || (search && childMatches))) {
      const childConfig = (value as Record<string, unknown>) ?? {};
      const childProps = prop.properties ?? {};
      const childEntries = Object.entries(childProps).sort(([a], [b]) =>
        a.localeCompare(b),
      );
      for (const [ck, cp] of childEntries) {
        const childId = `${key}.${ck}`;
        if (search && !matchesSearch(ck, cp)) continue;
        schemaRows.push({
          id: childId,
          path: [key, ck],
          depth: 1,
          key: ck,
          schema: cp,
          value: childConfig[ck],
          isGroup: false,
          isExpanded: false,
          isUnknown: false,
          // Schema declares this child, but the live config object doesn't have it —
          // it's rendered from the schema with an empty/default value.
          isAbsent: !(ck in childConfig),
        });
      }
      // Unknown children — keys present in the config but not declared by the
      // parent's schema properties (this is the only path that fires for
      // schema objects with `additionalProperties: true` and no `properties`).
      const unknownChildKeys = Object.keys(childConfig)
        .filter((ck) => !(ck in childProps))
        .sort((a, b) => a.localeCompare(b));
      for (const ck of unknownChildKeys) {
        const childId = `${key}.${ck}`;
        if (search && !ck.toLowerCase().includes(lc)) continue;
        schemaRows.push({
          id: childId,
          path: [key, ck],
          depth: 1,
          key: ck,
          schema: null,
          value: childConfig[ck],
          isGroup: false,
          isExpanded: false,
          isUnknown: true,
          isAbsent: false, // unknown children come from the live config, so present
        });
      }
    }
  }

  // Unknown top-level keys — sorted alphabetically. Keys matching known
  // integration codes (bokun, fareharbor, twilio, etc.) are filtered out:
  // they are managed via dedicated integration screens, not the config grid.
  const unknownTopKeys = Object.keys(config)
    .filter((k) => !(k in schemaProps) && !hiddenKeys.has(k))
    .sort((a, b) => a.localeCompare(b));
  for (const key of unknownTopKeys) {
    if (search && !key.toLowerCase().includes(lc)) continue;
    unknownRows.push({
      id: `__unknown.${key}`,
      path: [key],
      depth: 0,
      key,
      schema: null,
      value: config[key],
      isGroup: false,
      isExpanded: false,
      isUnknown: true,
      isAbsent: false, // unknown top-level keys come from the live config
    });
  }

  return { schema: schemaRows, unknown: unknownRows };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getDefaultValue(prop: SchemaProp): unknown {
  if (prop.default !== undefined) return prop.default;
  if (prop.type === 'boolean') return false;
  if (prop.type === 'integer' || prop.type === 'number') return 0;
  if (prop.type === 'string') return '';
  if (prop.type === 'array') return [];
  if (prop.type === 'object') {
    if (!prop.properties) return {};
    const obj: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(prop.properties)) {
      if (v.default !== undefined) obj[k] = v.default;
    }
    return obj;
  }
  return null;
}

function setNestedValue(
  config: Record<string, unknown>,
  path: string[],
  value: unknown,
): Record<string, unknown> {
  const [head, ...tail] = path;
  if (!head) return config;
  if (tail.length === 0) {
    return { ...config, [head]: value };
  }
  return {
    ...config,
    [head]: setNestedValue(
      (config[head] as Record<string, unknown>) ?? {},
      tail,
      value,
    ),
  };
}

/** Remove `path` from the config object, returning a new object.
 *  Removes the key entirely (not just sets it to undefined). */
function removeNestedKey(
  config: Record<string, unknown>,
  path: string[],
): Record<string, unknown> {
  const [head, ...tail] = path;
  if (!head) return config;
  if (tail.length === 0) {
    const next = { ...config };
    delete next[head];
    return next;
  }
  const childRaw = config[head];
  if (childRaw == null || typeof childRaw !== 'object' || Array.isArray(childRaw)) {
    return config;
  }
  return {
    ...config,
    [head]: removeNestedKey(childRaw as Record<string, unknown>, tail),
  };
}

/** Remove a batch of paths at once. */
function removePaths(
  config: Record<string, unknown>,
  paths: string[][],
): Record<string, unknown> {
  return paths.reduce((acc, p) => removeNestedKey(acc, p), config);
}

function formatType(prop: SchemaProp | null): string {
  if (!prop) return 'unknown';
  if (prop.enum) return `${prop.type ?? 'enum'} enum`;
  if (prop['x-unit']) return `${prop.type} (${prop['x-unit']})`;
  return prop.type ?? 'unknown';
}

// ---------------------------------------------------------------------------
// Value cell — type-aware inline editor
// ---------------------------------------------------------------------------

interface ValueCellProps {
  row: ConfigRow;
  readOnly: boolean;
  onChange: (v: unknown) => void;
}

function ChipArrayCell({
  value,
  onChange,
  readOnly,
  enumValues,
}: {
  value: unknown[];
  onChange: (v: unknown[]) => void;
  readOnly: boolean;
  enumValues?: unknown[];
}) {
  const [input, setInput] = useState('');

  if (enumValues) {
    const selected = (Array.isArray(value) ? value : []) as string[];
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        {enumValues.map((ev) => {
          const s = String(ev);
          const checked = selected.includes(s);
          return (
            <Chip
              key={s}
              label={s}
              size="small"
              color={checked ? 'primary' : 'default'}
              onClick={readOnly ? undefined : () =>
                onChange(checked ? selected.filter((x) => x !== s) : [...selected, s])
              }
              variant={checked ? 'filled' : 'outlined'}
            />
          );
        })}
      </Box>
    );
  }

  const arr = Array.isArray(value) ? value : [];
  function add() {
    const v = input.trim();
    if (!v) return;
    // Try to coerce to a number for numeric arrays
    const num = Number(v);
    onChange([...arr, Number.isNaN(num) ? v : num]);
    setInput('');
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
      {arr.map((item, i) => (
        <Chip
          key={i}
          label={String(item)}
          size="small"
          onDelete={readOnly ? undefined : () => onChange(arr.filter((_, j) => j !== i))}
        />
      ))}
      {!readOnly && (
        <TextField
          size="small"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }}
          placeholder="add…"
          sx={{ '& input': { py: 0.25, px: 0.75, width: 72, fontSize: 12 } }}
          variant="standard"
        />
      )}
    </Box>
  );
}

/** JSON-stringify a value safely; never returns undefined. */
function safePreview(value: unknown, max = 60): string {
  if (value === undefined) return '—';
  let s: string | undefined;
  try {
    s = JSON.stringify(value);
  } catch {
    s = String(value);
  }
  if (s === undefined) return '—';
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

function ValueCell({ row, readOnly, onChange }: ValueCellProps) {
  const { schema: prop, value } = row;

  if (row.isGroup) return null;

  // Check unknown FIRST — unknown rows have `schema: null` and would otherwise
  // hit the `!prop` short-circuit below.
  if (row.isUnknown || !prop) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
        {safePreview(value)}
      </Typography>
    );
  }

  const type = prop.type;
  const enumVals = prop.enum;

  // Boolean — Switch
  if (type === 'boolean') {
    return (
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            size="small"
            disabled={readOnly}
          />
        }
        label={
          <Typography variant="caption" color="text.secondary">
            {value ? 'true' : 'false'}
          </Typography>
        }
        sx={{ m: 0 }}
      />
    );
  }

  // Integer/number with enum — Select
  if ((type === 'integer' || type === 'number' || type === 'string') && enumVals) {
    return (
      <Select
        value={value ?? ''}
        onChange={(e) => {
          const v = e.target.value;
          onChange(type === 'integer' ? Number(v) : v);
        }}
        size="small"
        disabled={readOnly}
        sx={{
          width: '100%',
          maxWidth: 420,
          fontSize: 13,
          '& .MuiSelect-select': { py: 0.5 },
        }}
      >
        {enumVals.map((ev) => (
          <MenuItem key={String(ev)} value={ev as string | number}>
            {enumLabel(prop, ev) ?? String(ev)}
          </MenuItem>
        ))}
      </Select>
    );
  }

  // Integer — number input
  if (type === 'integer') {
    return (
      <TextField
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        size="small"
        disabled={readOnly}
        inputProps={{ step: 1 }}
        sx={{
          width: '100%',
          maxWidth: 240,
          '& input': { py: 0.5, fontSize: 13 },
        }}
      />
    );
  }

  // Number (float)
  if (type === 'number') {
    return (
      <TextField
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        size="small"
        disabled={readOnly}
        inputProps={{ step: 0.01 }}
        sx={{
          width: '100%',
          maxWidth: 240,
          '& input': { py: 0.5, fontSize: 13 },
        }}
      />
    );
  }

  // String
  if (type === 'string') {
    const str = String(value ?? '');
    return (
      <TextField
        value={str}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        disabled={readOnly}
        multiline={str.length > 80}
        maxRows={6}
        sx={{
          width: '100%',
          maxWidth: 720,
          '& input, & textarea': { py: 0.5, fontSize: 13 },
        }}
      />
    );
  }

  // Array
  if (type === 'array') {
    // Special case: auth_methods / client_auth_methods are arrays of objects
    // with a known shape. The schema doesn't define `items` for them, so we
    // dispatch by top-level key name. Match only at depth 0 to avoid hitting
    // a same-named child of some unrelated nested object.
    if (
      row.depth === 0 &&
      (row.key === 'auth_methods' || row.key === 'client_auth_methods')
    ) {
      return (
        <AuthMethodsCell
          value={(Array.isArray(value) ? value : []) as AuthMethod[]}
          readOnly={readOnly}
          onChange={onChange as (v: AuthMethod[]) => void}
        />
      );
    }

    if (enumVals) {
      return (
        <ChipArrayCell
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
          readOnly={readOnly}
          enumValues={enumVals}
        />
      );
    }
    return (
      <ChipArrayCell
        value={Array.isArray(value) ? value : []}
        onChange={onChange}
        readOnly={readOnly}
      />
    );
  }

  // Object / null — schema-typed but not directly editable here (nested
  // objects beyond depth 1 fall through to the Raw tab). Show a safe preview.
  return (
    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
      {safePreview(value)}
    </Typography>
  );
}

// ---------------------------------------------------------------------------
// Help pane
// ---------------------------------------------------------------------------

function HelpPane({ row }: { row: ConfigRow }) {
  const prop = row.schema;
  return (
    <Box sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
      <Typography variant="subtitle2" sx={{ fontFamily: 'monospace', mb: 0.5 }}>
        {row.path.join('.')}
      </Typography>
      <Chip label={formatType(prop)} size="small" sx={{ mb: 1.5 }} />

      {prop?.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {prop.description}
        </Typography>
      )}

      {prop?.['x-unit'] && (
        <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
          <strong>Unit:</strong> {prop['x-unit']}
        </Typography>
      )}

      {prop?.default !== undefined && (
        <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
          <strong>Default:</strong>{' '}
          <Box component="span" sx={{ fontFamily: 'monospace' }}>
            {JSON.stringify(prop.default)}
          </Box>
        </Typography>
      )}

      {prop?.['x-default-source'] && (
        <Typography variant="caption" display="block" color="text.disabled" sx={{ mb: 1 }}>
          Source: {prop['x-default-source']}
        </Typography>
      )}

      {prop?.enum && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
            <strong>Allowed values:</strong>
          </Typography>
          {prop.enum.map((ev) => {
            const label = enumLabel(prop, ev);
            return (
              <Box key={String(ev)} sx={{ display: 'flex', gap: 1, mb: 0.25 }}>
                <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12, minWidth: 40 }}>
                  {JSON.stringify(ev)}
                </Box>
                {label && (
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {prop?.['x-format-hint'] && (
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
          Format: {prop['x-format-hint']}
        </Typography>
      )}

      {prop?.['$comment'] && (
        <Typography variant="caption" display="block" color="text.disabled" sx={{ mt: 1 }}>
          {prop['$comment']}
        </Typography>
      )}

      {row.isUnknown && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          This key is not defined in the schema. Edit via the Raw tab.
        </Alert>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Row renderer
// ---------------------------------------------------------------------------

const ROW_HEIGHT = 40;
const INDENT = 24;

function ConfigRowItem({
  row,
  selected,
  readOnly,
  deletable,
  checked,
  onSelect,
  onToggleExpand,
  onChange,
  onRequestDelete,
  onToggleCheck,
}: {
  row: ConfigRow;
  selected: boolean;
  /** Value cell is read-only (still allows row deletion). */
  readOnly: boolean;
  /** Whether to render the delete button + selection checkbox. */
  deletable: boolean;
  checked: boolean;
  onSelect: () => void;
  onToggleExpand: () => void;
  onChange: (v: unknown) => void;
  onRequestDelete: () => void;
  onToggleCheck: () => void;
}) {
  return (
    <Box
      onClick={onSelect}
      sx={{
        display: 'flex',
        alignItems: 'center',
        minHeight: ROW_HEIGHT,
        px: 1,
        pl: `${row.depth * INDENT + 4}px`,
        gap: 1,
        cursor: 'pointer',
        borderRadius: 1,
        bgcolor: selected ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Expand chevron */}
      <Box sx={{ width: 28, flexShrink: 0 }}>
        {row.isGroup ? (
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
            sx={{ p: 0.25 }}
          >
            {row.isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        ) : null}
      </Box>

      {/* Key name — wide enough for the longest schema keys (~45 chars at 12px monospace) */}
      <Box sx={{ width: 380, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Tooltip title={row.key} placement="top-start" arrow disableHoverListener={row.key.length < 40}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontSize: 12,
              fontWeight: row.depth === 0 ? 600 : 400,
              // Light gray when the schema declares this nested key but it is
              // absent from the live config (rendered with an empty/default
              // value); black once the key is actually present.
              color: row.isAbsent ? 'text.disabled' : 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {row.key}
          </Typography>
        </Tooltip>
        {row.isUnknown && (
          <Tooltip title="Not in schema — read-only in Assisted editor">
            <WarningAmberIcon sx={{ fontSize: 14, color: 'warning.main' }} />
          </Tooltip>
        )}
      </Box>

      {/* Value cell */}
      <Box sx={{ flex: 1, minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
        {!row.isGroup && (
          <ValueCell row={row} readOnly={readOnly || row.isUnknown} onChange={onChange} />
        )}
        {row.isGroup && (
          <Typography variant="caption" color="text.disabled">
            {row.isExpanded ? 'expanded' : `{${Object.keys((row.value as Record<string, unknown>) ?? {}).length} keys}`}
          </Typography>
        )}
      </Box>

      {/* Type badge */}
      <Box sx={{ width: 80, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
        {row.schema && (
          <Chip
            label={row.schema.type ?? '?'}
            size="small"
            variant="outlined"
            sx={{ fontSize: 10, height: 18 }}
          />
        )}
      </Box>

      {/* Per-row delete button */}
      <Box sx={{ width: 36, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
        {deletable && (
          <Tooltip title={`Delete ${row.path.join('.')}`} placement="top" arrow>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onRequestDelete(); }}
              sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'error.main' } }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Right-most: selection checkbox for batch deletion */}
      <Box sx={{ width: 36, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
        {deletable && (
          <Checkbox
            size="small"
            checked={checked}
            onClick={(e) => e.stopPropagation()}
            onChange={onToggleCheck}
            sx={{ p: 0.25 }}
          />
        )}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface Props {
  to: TourOperatorDetail;
  onSaved: () => void;
  /** Lifted edit state — shared with sibling Configuration subtabs so a
   *  merge from Change History (or a Raw-tab edit) is visible here too. */
  currentConfig: Record<string, unknown>;
  setCurrentConfig: (next: Record<string, unknown>) => void;
}

export function AssistedConfigEditor(props: Props) {
  // The served schema gates the whole grid — every row, help pane, and the
  // add-property picker derive from it.
  const { schemaProps, isLoading, error, refetch } = useTopConfigSchema();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !schemaProps) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        Couldn’t load the configuration schema:{' '}
        {error instanceof Error ? error.message : 'no schema returned'}
      </Alert>
    );
  }
  return <AssistedConfigEditorInner {...props} schemaProps={schemaProps} />;
}

function AssistedConfigEditorInner({
  to,
  onSaved,
  currentConfig,
  setCurrentConfig,
  schemaProps: SCHEMA_PROPS,
}: Props & { schemaProps: Record<string, SchemaProp> }) {
  const snackbar = useSnackbar();
  const { hasPerm } = usePermissions();
  const canEdit = hasPerm('console_top_manage');

  const originalConfig = (to.config as Record<string, unknown> | null) ?? {};
  // Alias kept for minimal-diff refactor — the actual edit state lives in
  // the parent now so siblings (Change history, Raw editor) can mutate it.
  const localConfig = currentConfig;
  const setLocalConfig = (
    next: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>),
  ) => {
    setCurrentConfig(
      typeof next === 'function'
        ? (next as (p: Record<string, unknown>) => Record<string, unknown>)(currentConfig)
        : next,
    );
  };
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [addKey, setAddKey] = useState<string | null>(null);
  // Checked row ids (set of row.id strings) for batch operations.
  const [checked, setChecked] = useState<Set<string>>(new Set());
  // Pending deletion confirmation — `null` means no dialog is open.
  // `single` carries one row's id+path; `batch` carries the list of paths.
  const [pendingDelete, setPendingDelete] = useState<
    | { kind: 'single'; rowId: string; path: string[] }
    | { kind: 'batch'; paths: string[][]; rowIds: string[] }
    | null
  >(null);
  // Open state for the "Import from another tour operator" wizard.
  const [importOpen, setImportOpen] = useState(false);

  const isDirty = JSON.stringify(localConfig) !== JSON.stringify(originalConfig);

  // Catalog of integration ids (bokun, fareharbor, twilio, …) — these
  // config keys are managed via dedicated integration screens and are
  // hidden from the "Unknown" section of this grid. Cached for the
  // session; the list rarely changes.
  const integrationsQuery = useQuery({
    queryKey: ['integrations', 'list'],
    queryFn: () => integrationsApi.list(),
    staleTime: 60 * 60 * 1000,
  });
  const hiddenIntegrationKeys = useMemo(
    () => {
      const ids = (integrationsQuery.data?.results ?? []).map((i) => i.id);
      // `curatedplanet` is our own integration — surface it as a structured
      // nested object so ops can drill into its sub-keys, rather than hiding
      // it alongside third-party opaque blobs.
      return new Set(ids.filter((id) => id !== 'curatedplanet'));
    },
    [integrationsQuery.data],
  );

  const { schema: schemaRows, unknown: unknownRows } = useMemo(
    () => buildRows(SCHEMA_PROPS, localConfig, expanded, search, hiddenIntegrationKeys),
    [SCHEMA_PROPS, localConfig, expanded, search, hiddenIntegrationKeys],
  );

  const selectedRow = [...schemaRows, ...unknownRows].find((r) => r.id === selected);

  const availableToAdd = useMemo(
    () =>
      Object.entries(SCHEMA_PROPS)
        .filter(([k]) => !(k in localConfig))
        .map(([k, v]) => ({ key: k, prop: v }))
        .sort((a, b) => a.key.localeCompare(b.key)),
    [SCHEMA_PROPS, localConfig],
  );

  const toggleExpand = useCallback((id: string) => {
    setExpanded((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  }, []);

  function handleChange(path: string[], value: unknown) {
    setLocalConfig((prev) => setNestedValue(prev, path, value));
  }

  function handleAdd() {
    if (!addKey) return;
    const prop = SCHEMA_PROPS[addKey];
    if (!prop) return;
    const defaultValue = getDefaultValue(prop);
    setLocalConfig((prev) => ({ ...prev, [addKey]: defaultValue }));
    if (prop.type === 'object') {
      setExpanded((prev) => new Set([...prev, addKey]));
    }
    setSelected(addKey);
    setAddKey(null);
  }

  // ---- Selection (checkboxes) ----
  const toggleCheck = useCallback((id: string) => {
    setChecked((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  }, []);
  const clearChecked = useCallback(() => setChecked(new Set()), []);

  // ---- Deletion ----
  function requestSingleDelete(rowId: string, path: string[]) {
    setPendingDelete({ kind: 'single', rowId, path });
  }

  function requestBatchDelete() {
    if (checked.size === 0) return;
    // Resolve checked row ids → paths via the currently visible rows.
    const allRows = [...schemaRows, ...unknownRows];
    const toDelete = allRows.filter((r) => checked.has(r.id));
    if (toDelete.length === 0) return;
    setPendingDelete({
      kind: 'batch',
      paths: toDelete.map((r) => r.path),
      rowIds: toDelete.map((r) => r.id),
    });
  }

  function confirmDelete() {
    if (!pendingDelete) return;
    if (pendingDelete.kind === 'single') {
      setLocalConfig((prev) => removeNestedKey(prev, pendingDelete.path));
      // Drop from selection set + currently-selected-for-detail-pane
      setChecked((prev) => {
        const s = new Set(prev);
        s.delete(pendingDelete.rowId);
        return s;
      });
      if (selected === pendingDelete.rowId) setSelected(null);
    } else {
      setLocalConfig((prev) => removePaths(prev, pendingDelete.paths));
      setChecked((prev) => {
        const s = new Set(prev);
        pendingDelete.rowIds.forEach((id) => s.delete(id));
        return s;
      });
      if (selected && pendingDelete.rowIds.includes(selected)) setSelected(null);
    }
    setPendingDelete(null);
  }

  // Dialog visibility + error surface for the save confirmation flow.
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: {
      config: Record<string, unknown>;
      comment: string;
      ifUnmodifiedSince: string | null;
    }) =>
      topsApi.update(to.id, {
        config: body.config,
        comment: body.comment,
        if_unmodified_since: body.ifUnmodifiedSince,
      }),
    onSuccess: () => {
      snackbar.show('Configuration saved.', 'success');
      setSaveDialogOpen(false);
      setSaveError(null);
      onSaved();
    },
    onError: (e: unknown) => {
      // 409 from the backend means someone else's write landed first; surface
      // it inline in the dialog rather than closing on error.
      if (e instanceof ConsoleApiError) {
        if (e.status === 409) {
          setSaveError(e.body?.detail ?? 'Configuration has been modified — please refresh.');
        } else {
          setSaveError(e.message);
        }
      } else {
        setSaveError('Save failed.');
      }
    },
  });

  const listHeight = 'calc(100vh - 380px)';

  return (
    <Box sx={{ display: 'flex', gap: 2, height: listHeight }}>
      {/* Left: list */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Toolbar */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexShrink: 0, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search key or description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 280 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          {canEdit && (
            <>
              <AddPropertyPicker
                options={availableToAdd.map((o) => ({ key: o.key, type: o.prop.type }))}
                value={addKey}
                onChange={setAddKey}
                renderHelp={(key) => (
                  <HelpPane
                    row={{
                      id: key,
                      path: [key],
                      depth: 0,
                      key,
                      schema: SCHEMA_PROPS[key] ?? null,
                      value: undefined,
                      isGroup: false,
                      isExpanded: false,
                      isUnknown: false,
                      isAbsent: false,
                    }}
                  />
                )}
              />
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={!addKey}
                onClick={handleAdd}
                size="small"
              >
                Add
              </Button>
              <Tooltip title="Import configuration keys from another tour operator">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<FileDownloadOutlinedIcon />}
                    onClick={() => setImportOpen(true)}
                    size="small"
                  >
                    Import…
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            size="small"
            disabled={!canEdit || !isDirty || mutation.isPending}
            onClick={() => { setSaveError(null); setSaveDialogOpen(true); }}
          >
            {mutation.isPending ? 'Saving…' : 'Save changes…'}
          </Button>
        </Box>

        {/* Row count */}
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, flexShrink: 0 }}>
          {schemaRows.length} schema key{schemaRows.length !== 1 ? 's' : ''}
          {unknownRows.length > 0 && ` · ${unknownRows.length} unknown`}
        </Typography>

        {/* Batch action toolbar — visible when ≥1 row is selected */}
        {checked.size > 0 && canEdit && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1,
              p: 1,
              bgcolor: 'action.selected',
              borderRadius: 1,
              flexShrink: 0,
            }}
          >
            <Typography variant="body2">
              <strong>{checked.size}</strong> key{checked.size !== 1 ? 's' : ''} selected
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" onClick={clearChecked}>Clear</Button>
            <Button
              size="small"
              color="error"
              variant="contained"
              startIcon={<DeleteOutlineIcon />}
              onClick={requestBatchDelete}
            >
              Delete selected
            </Button>
          </Box>
        )}

        {/* Scrollable list */}
        <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {/* Schema rows */}
          {schemaRows.length === 0 && !unknownRows.length && (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              {search ? 'No keys match your search.' : 'No configuration keys.'}
            </Typography>
          )}
          {schemaRows.map((row) => (
            <ConfigRowItem
              key={row.id}
              row={row}
              selected={selected === row.id}
              readOnly={!canEdit}
              deletable={canEdit}
              checked={checked.has(row.id)}
              onSelect={() => setSelected(row.id)}
              onToggleExpand={() => toggleExpand(row.id)}
              onChange={(v) => handleChange(row.path, v)}
              onRequestDelete={() => requestSingleDelete(row.id, row.path)}
              onToggleCheck={() => toggleCheck(row.id)}
            />
          ))}

          {/* Unknown / integration keys section */}
          {unknownRows.length > 0 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 1, mt: 1 }}>
                <Divider sx={{ flex: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Unknown / Integration keys (read-only in Assisted)
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Box>
              {unknownRows.map((row) => (
                <ConfigRowItem
                  key={row.id}
                  row={row}
                  selected={selected === row.id}
                  readOnly
                  deletable={canEdit}
                  checked={checked.has(row.id)}
                  onSelect={() => setSelected(row.id)}
                  onToggleExpand={() => {}}
                  onChange={() => {}}
                  onRequestDelete={() => requestSingleDelete(row.id, row.path)}
                  onToggleCheck={() => toggleCheck(row.id)}
                />
              ))}
            </>
          )}
        </Box>
      </Box>

      {/* Right: help pane */}
      <Box
        sx={{
          width: 300,
          flexShrink: 0,
          borderLeft: 1,
          borderColor: 'divider',
          overflowY: 'auto',
          bgcolor: 'background.paper',
        }}
      >
        {selectedRow ? (
          <HelpPane row={selectedRow} />
        ) : (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Select a row to see its description, type, and default value.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Import-from-another-TO wizard */}
      <ImportConfigWizard
        open={importOpen}
        targetId={to.id}
        targetConfig={localConfig}
        onMerge={(merged) => {
          setLocalConfig(merged);
          // Auto-expand every dict whose subtree the merge wrote into, so the
          // user can immediately see the imported values inline.
          setExpanded((prev) => {
            const next = new Set(prev);
            for (const k of Object.keys(merged)) {
              const v = merged[k];
              if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
                next.add(k);
              }
            }
            return next;
          });
        }}
        onClose={() => setImportOpen(false)}
      />

      {/* Delete confirmation dialog (single or batch) */}
      {pendingDelete && (
        <ConfirmDialog
          open
          destructive
          title={
            pendingDelete.kind === 'single'
              ? 'Remove configuration key?'
              : `Remove ${pendingDelete.paths.length} configuration keys?`
          }
          message={
            pendingDelete.kind === 'single'
              ? `This will remove "${pendingDelete.path.join('.')}" from the tour operator config. The change is not saved until you click Save changes.`
              : `This will remove these keys from the tour operator config:\n\n${pendingDelete.paths.map((p) => '• ' + p.join('.')).join('\n')}\n\nThe change is not saved until you click Save changes.`
          }
          confirmLabel="Remove"
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}

      {/* Save-changes confirmation dialog */}
      <SaveConfigChangesDialog
        open={saveDialogOpen}
        tourOperatorId={to.id}
        originalConfig={originalConfig}
        pendingConfig={localConfig}
        loadedConfigTs={to.config_last_modified_ts}
        saving={mutation.isPending}
        saveError={saveError}
        onConfirm={({ comment, ifUnmodifiedSince }) =>
          mutation.mutate({ config: localConfig, comment, ifUnmodifiedSince })
        }
        onCancel={() => { setSaveDialogOpen(false); setSaveError(null); }}
      />
    </Box>
  );
}
